/**
 * Cart Panel Component
 *
 * Renders interactive cart panel for display in chat
 */

/**
 * Create a cart item element
 * @param {Object} item - Cart item data
 * @param {Object} callbacks - Event callbacks { onQuantityChange, onRemove }
 * @returns {HTMLElement}
 */
export function createCartItem(item, callbacks = {}) {
    const itemEl = document.createElement('div');
    itemEl.className = 'fyncall-cart-panel-item';
    itemEl.setAttribute('data-variant-id', item.variant_id);

    const hasDiscount = item.compare_at_price && item.compare_at_price > item.price;

    itemEl.innerHTML = `
        <div class="fyncall-cart-panel-item-image">
            ${item.image_url ? `<img src="${item.image_url}" alt="${item.name}" onerror="this.parentElement.innerHTML='📦'">` : '📦'}
        </div>
        <div class="fyncall-cart-panel-item-details">
            <div class="fyncall-cart-panel-item-name">${item.name}</div>
            ${item.variant_title ? `<div class="fyncall-cart-panel-item-variant">${item.variant_title}</div>` : ''}
            <div class="fyncall-cart-panel-item-price">
                <span class="fyncall-cart-panel-item-current">$${parseFloat(item.price).toFixed(2)}</span>
                ${hasDiscount ? `<span class="fyncall-cart-panel-item-original">$${parseFloat(item.compare_at_price).toFixed(2)}</span>` : ''}
            </div>
        </div>
        <div class="fyncall-cart-panel-item-actions">
            <div class="fyncall-cart-panel-quantity">
                <button class="fyncall-cart-panel-qty-btn minus" data-action="decrease" data-variant-id="${item.variant_id}">−</button>
                <span class="fyncall-cart-panel-qty-value">${item.quantity}</span>
                <button class="fyncall-cart-panel-qty-btn plus" data-action="increase" data-variant-id="${item.variant_id}">+</button>
            </div>
            <div class="fyncall-cart-panel-item-total">$${parseFloat(item.line_total).toFixed(2)}</div>
            <button class="fyncall-cart-panel-remove" data-variant-id="${item.variant_id}" title="Remove item">🗑️</button>
        </div>
    `;

    // Add event listeners
    const minusBtn = itemEl.querySelector('.minus');
    const plusBtn = itemEl.querySelector('.plus');
    const removeBtn = itemEl.querySelector('.fyncall-cart-panel-remove');

    if (callbacks.onQuantityChange) {
        minusBtn.onclick = () => callbacks.onQuantityChange(item, -1, itemEl);
        plusBtn.onclick = () => callbacks.onQuantityChange(item, 1, itemEl);
    }
    if (callbacks.onRemove) {
        removeBtn.onclick = () => callbacks.onRemove(item, itemEl);
    }

    return itemEl;
}

/**
 * Create empty cart HTML
 * @param {Function} onBrowse - Callback when browse button clicked
 * @returns {string} HTML string
 */
export function createEmptyCartHTML(onBrowse) {
    return `
        <div class="fyncall-cart-panel-empty">
            <div class="fyncall-cart-panel-empty-icon">🛒</div>
            <div class="fyncall-cart-panel-empty-text">Your cart is empty</div>
            <button class="fyncall-cart-panel-shop-btn">Browse Products</button>
        </div>
    `;
}

/**
 * Create cart panel card
 * @param {Object} data - Cart data
 * @param {Object} callbacks - Event callbacks
 * @returns {HTMLElement}
 */
export function createCartPanelCard(data, callbacks = {}) {
    const container = document.createElement('div');
    container.className = 'fyncall-cart-panel-card';

    // Handle empty cart
    if (data.is_empty || !data.items || data.items.length === 0) {
        container.innerHTML = createEmptyCartHTML();
        const browseBtn = container.querySelector('.fyncall-cart-panel-shop-btn');
        if (browseBtn && callbacks.onBrowse) {
            browseBtn.onclick = () => callbacks.onBrowse();
        }
        return container;
    }

    // Cart header
    const header = document.createElement('div');
    header.className = 'fyncall-cart-panel-header';
    header.innerHTML = `
        <span class="fyncall-cart-panel-title">🛒 Your Cart</span>
        <span class="fyncall-cart-panel-count">${data.item_count} item${data.item_count !== 1 ? 's' : ''}</span>
    `;
    container.appendChild(header);

    // Cart items
    const itemsContainer = document.createElement('div');
    itemsContainer.className = 'fyncall-cart-panel-items';

    data.items.forEach(item => {
        const itemEl = createCartItem(item, {
            onQuantityChange: callbacks.onQuantityChange,
            onRemove: callbacks.onRemove
        });
        itemsContainer.appendChild(itemEl);
    });
    container.appendChild(itemsContainer);

    // Promo code section
    if (data.promo_available) {
        const promoSection = createPromoSection(callbacks.onApplyPromo);
        container.appendChild(promoSection);
    }

    // Savings display
    if (data.total_savings && data.total_savings > 0) {
        const savings = document.createElement('div');
        savings.className = 'fyncall-cart-panel-savings';
        savings.innerHTML = `
            <span>💰 You're saving</span>
            <span class="fyncall-cart-panel-savings-amount">$${parseFloat(data.total_savings).toFixed(2)}</span>
        `;
        container.appendChild(savings);
    }

    // Order summary
    const summary = createOrderSummary(data);
    container.appendChild(summary);

    // Actions
    const actions = createCartActions(callbacks);
    container.appendChild(actions);

    return container;
}

/**
 * Create free shipping progress bar
 * @param {Object} data - Cart data with shipping info
 * @returns {HTMLElement}
 */
export function createFreeShippingProgress(data) {
    const progress = document.createElement('div');
    progress.className = 'fyncall-cart-panel-shipping-progress';
    const percentComplete = Math.min((data.total / data.free_shipping_threshold) * 100, 100);
    const remaining = Math.max(data.free_shipping_threshold - data.total, 0);

    progress.innerHTML = `
        <div class="fyncall-cart-panel-shipping-bar">
            <div class="fyncall-cart-panel-shipping-fill" style="width: ${percentComplete}%"></div>
        </div>
        <div class="fyncall-cart-panel-shipping-text ${data.qualifies_for_free_shipping ? 'achieved' : ''}">
            ${data.qualifies_for_free_shipping
                ? '🎉 You qualify for FREE shipping!'
                : `Add $${remaining.toFixed(2)} more for FREE shipping`}
        </div>
    `;
    return progress;
}

/**
 * Create promo code section
 * @param {Function} onApply - Callback when promo applied
 * @returns {HTMLElement}
 */
export function createPromoSection(onApply) {
    const promoSection = document.createElement('div');
    promoSection.className = 'fyncall-cart-panel-promo';
    promoSection.innerHTML = `
        <div class="fyncall-cart-panel-promo-input-wrapper">
            <input type="text" class="fyncall-cart-panel-promo-input" placeholder="Enter promo code">
            <button class="fyncall-cart-panel-promo-apply">Apply</button>
        </div>
    `;

    if (onApply) {
        const applyBtn = promoSection.querySelector('.fyncall-cart-panel-promo-apply');
        const promoInput = promoSection.querySelector('.fyncall-cart-panel-promo-input');
        applyBtn.onclick = () => onApply(promoInput.value, promoSection);
    }

    return promoSection;
}

/**
 * Create order summary section
 * @param {Object} data - Cart data
 * @returns {HTMLElement}
 */
export function createOrderSummary(data) {
    const summary = document.createElement('div');
    summary.className = 'fyncall-cart-panel-summary';
    summary.innerHTML = `
        <div class="fyncall-cart-panel-summary-row">
            <span>Subtotal</span>
            <span>$${parseFloat(data.subtotal).toFixed(2)}</span>
        </div>
        <div class="fyncall-cart-panel-summary-row shipping">
            <span>Shipping</span>
            <span>${data.qualifies_for_free_shipping ? 'FREE' : 'Calculated at checkout'}</span>
        </div>
        <div class="fyncall-cart-panel-summary-row total">
            <span>Total</span>
            <span class="fyncall-cart-panel-total-amount">${data.currency} $${parseFloat(data.total).toFixed(2)}</span>
        </div>
    `;
    return summary;
}

/**
 * Create cart action buttons
 * @param {Object} callbacks - { onCheckout, onContinueShopping }
 * @returns {HTMLElement}
 */
export function createCartActions(callbacks = {}) {
    const actions = document.createElement('div');
    actions.className = 'fyncall-cart-panel-actions';

    const checkoutBtn = document.createElement('button');
    checkoutBtn.className = 'fyncall-cart-panel-checkout-btn';
    checkoutBtn.textContent = 'Proceed to Checkout';
    if (callbacks.onCheckout) {
        checkoutBtn.onclick = callbacks.onCheckout;
    }

    const continueBtn = document.createElement('button');
    continueBtn.className = 'fyncall-cart-panel-continue-btn';
    continueBtn.textContent = 'Continue Shopping';
    if (callbacks.onContinueShopping) {
        continueBtn.onclick = callbacks.onContinueShopping;
    }

    actions.appendChild(checkoutBtn);
    actions.appendChild(continueBtn);

    return actions;
}

/**
 * Update cart panel totals after cart changes
 * @param {HTMLElement} cartCard - Cart panel card element
 * @param {Object} cart - Updated cart data from Shopify
 * @param {number} freeShippingThreshold - Free shipping threshold (default 75)
 */
export function updateCartPanelTotals(cartCard, cart, freeShippingThreshold = 75) {
    if (!cartCard || !cart) return;

    const total = cart.total_price / 100 || 0;
    const itemCount = cart.item_count || 0;

    // Update header count
    const headerCount = cartCard.querySelector('.fyncall-cart-panel-count');
    if (headerCount) {
        headerCount.textContent = `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
    }

    // Update subtotal
    const subtotalEl = cartCard.querySelector('.fyncall-cart-panel-summary-row:first-child span:last-child');
    if (subtotalEl) {
        subtotalEl.textContent = '$' + total.toFixed(2);
    }

    // Update total
    const totalEl = cartCard.querySelector('.fyncall-cart-panel-total-amount');
    if (totalEl) {
        const currency = cart.currency || 'USD';
        totalEl.textContent = `${currency} $${total.toFixed(2)}`;
    }

    // Update free shipping progress
    const shippingProgress = cartCard.querySelector('.fyncall-cart-panel-shipping-progress');
    if (shippingProgress) {
        const percentComplete = Math.min((total / freeShippingThreshold) * 100, 100);
        const remaining = Math.max(freeShippingThreshold - total, 0);
        const qualifies = total >= freeShippingThreshold;

        const fillEl = shippingProgress.querySelector('.fyncall-cart-panel-shipping-fill');
        if (fillEl) {
            fillEl.style.width = percentComplete + '%';
        }

        const textEl = shippingProgress.querySelector('.fyncall-cart-panel-shipping-text');
        if (textEl) {
            textEl.textContent = qualifies
                ? '🎉 You qualify for FREE shipping!'
                : `Add $${remaining.toFixed(2)} more for FREE shipping`;
            textEl.classList.toggle('achieved', qualifies);
        }
    }

    // Update shipping in summary
    const shippingEl = cartCard.querySelector('.fyncall-cart-panel-summary-row.shipping span:last-child');
    if (shippingEl) {
        shippingEl.textContent = total >= freeShippingThreshold ? 'FREE' : 'Calculated at checkout';
    }
}

/**
 * Update cart item line total
 * @param {HTMLElement} itemEl - Item element
 * @param {number} quantity - New quantity
 * @param {number} price - Item price
 */
export function updateCartItemTotal(itemEl, quantity, price) {
    const lineTotal = itemEl.querySelector('.fyncall-cart-panel-item-total');
    if (lineTotal) {
        lineTotal.textContent = '$' + (price * quantity).toFixed(2);
    }
    const qtyValue = itemEl.querySelector('.fyncall-cart-panel-qty-value');
    if (qtyValue) {
        qtyValue.textContent = quantity;
    }
}

/**
 * Show empty cart state
 * @param {HTMLElement} cartCard - Cart panel card element
 * @param {Function} onBrowse - Browse callback
 */
export function showEmptyCartState(cartCard, onBrowse) {
    if (!cartCard) return;
    cartCard.innerHTML = createEmptyCartHTML();
    const browseBtn = cartCard.querySelector('.fyncall-cart-panel-shop-btn');
    if (browseBtn && onBrowse) {
        browseBtn.onclick = onBrowse;
    }
}
